import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
  AccountCircle,
  Logout,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import UserAvatar from '../common/UserAvatar';

interface HeaderProps {
  onMenuClick: () => void;
  onSidebarToggle: () => void;
  sidebarCollapsed: boolean;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick, onSidebarToggle, sidebarCollapsed }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/login');
  };

  const getRoleLabel = (role: string): string => {
    const roleLabels: Record<string, string> = {
      admin: 'ผู้ดูแลระบบ',
      manager: 'ผู้จัดการ',
      agent: 'ตัวแทน',
      finance: 'การเงิน',
      customer_service: 'บริการลูกค้า',
    };
    return roleLabels[role] || role;
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: '#1976d2',
        width: '100%',
        borderRadius: 0,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        margin: 0,
        padding: 0,
        top: 0,
        left: 0,
      }}
    >
      <Toolbar>
        {/* Mobile hamburger */}
        <IconButton
          color="inherit"
          aria-label="เปิดเมนู"
          edge="start"
          onClick={onMenuClick}
          sx={{
            mr: 2,
            display: { xs: 'flex', md: 'none' },
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Desktop sidebar collapse toggle */}
        <IconButton
          color="inherit"
          aria-label={sidebarCollapsed ? 'ขยายแถบด้านข้าง' : 'ยุบแถบด้านข้าง'}
          edge="start"
          onClick={onSidebarToggle}
          sx={{
            mr: 2,
            display: { xs: 'none', md: 'flex' },
          }}
        >
          {sidebarCollapsed ? <MenuIcon /> : <MenuOpenIcon />}
        </IconButton>

        <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
          ระบบบริหารงานนายหน้าประกันภัย
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="body2">{user?.full_name}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {user?.role && getRoleLabel(user.role)}
            </Typography>
          </Box>

          <IconButton
            size="large"
            aria-label="บัญชีผู้ใช้"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenuOpen}
            color="inherit"
          >
            <UserAvatar user={user ?? { full_name: '' }} size={32} />
          </IconButton>

          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2">{user?.full_name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => { handleMenuClose(); navigate('/profile'); }}>
              <AccountCircle sx={{ mr: 1 }} />
              โปรไฟล์
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 1 }} />
              ออกจากระบบ
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
